import { useMemo } from 'react';
import BigNumber from 'bignumber.js';
import { getAllOperators } from '@/shared/graphql/cache/delegation';
import { getOperatorApys } from '@/shared/graphql/explorer/delegation';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import {
  calculateDelegatorApy,
  calculateOperatorDailyCommission,
  type DelegationApy,
  FALLBACK_ADDTL_DELEGATION,
} from '@/shared/utils/apy';
import TokenAmount from '@/shared/utils/TokenAmount';

export type OperatorSummary = NonNullable<
  ReturnType<typeof useOperatorsAndAuction>['operators']
>[number];

const weights = [5, 3, 1, 1, 1, 0.5, 0.5, 0.5, 0.5, 0.5] as const satisfies { length: 10 };

/** @deprecated Use `useOperatorsAndAuctionV2` instead. This hook will be removed in the future. */
export default function useOperatorsAndAuction({
  delegatorIdSs58,
  refetchInterval,
}: {
  delegatorIdSs58?: `cF${string}`;
  refetchInterval?: number;
} = {}) {
  const { data: cacheData, isLoading } = useGqlQuery(getAllOperators, {
    context: { clientName: 'statechainCache' },
    refetchInterval,
  });
  const { data: epochs } = useGqlQuery(getOperatorApys, {
    context: { clientName: 'processor' },
    refetchInterval,
    select: (data) => data.allEpoches!.nodes,
  });

  return useMemo(() => {
    if (!cacheData || !cacheData.auction || !cacheData.operators) return { isLoading };

    const auction = {
      minActiveBid: TokenAmount.fromAsset(cacheData.auction.minActiveBid, 'Flip') ?? undefined,
      blockReward: TokenAmount.fromAsset(cacheData.auction.blockReward, 'Flip'),
    };

    const operators = cacheData.operators.nodes
      .toSorted((a, b) => b.allValidators.totalCount - a.allValidators.totalCount)
      .map((o) => {
        let currentEpochDelegatorApy: Extract<DelegationApy, { type: 'apyPct' | 'n/a' }> = {
          type: 'n/a',
        };
        let currentEpochOperatorApy = 0;

        if (o.currentAuthorities.totalCount > 0) {
          const currentDelegation = TokenAmount.fromAsset(
            o.delegations.aggregates?.sum?.activeAmount ?? 0,
            'Flip',
          );
          const args = [
            {
              minActiveBid: TokenAmount.fromAsset(cacheData.auction?.activeBond ?? 0, 'Flip'),
              // the block reward can change in the middle of an epoch
              blockReward: auction.blockReward,
            },
            TokenAmount.ZERO,
            o.currentAuthorities.totalCount,
            TokenAmount.fromAsset(o.currentAuthorities.aggregates?.sum?.activeAmount ?? 0, 'Flip'),
            currentDelegation,
            o.activeDelegationFeeBps,
            cacheData.currentAuthorities?.totalCount,
          ] as const;

          if (currentDelegation.gt(0)) {
            currentEpochDelegatorApy = { type: 'apyPct', apyPct: calculateDelegatorApy(...args) };
          }
          const totalBond = new BigNumber(o.allValidators.aggregates?.sum?.bond ?? 0);
          currentEpochOperatorApy = totalBond.gt(0)
            ? calculateOperatorDailyCommission(...args)
                .value.times(365)
                .div(totalBond)
                .toNumber()
            : 0;
        }

        const isDelegationAllowed = o.openDelegationEnabled
          ? // if open delegation is enabled, this is a blocklist
            delegatorIdSs58 === undefined ||
            !(o.exceptionList as `cF${string}`[]).includes(delegatorIdSs58)
          : // otherwise it is an allow list
            delegatorIdSs58 !== undefined &&
            (o.exceptionList as `cF${string}`[]).includes(delegatorIdSs58);

        let totalUpcomingStake = TokenAmount.fromAsset(
          o.upcomingAuthorities.aggregates?.sum?.upcomingAmount ?? 0,
          'Flip',
        );

        let totalUpcomingDelegation = TokenAmount.fromAsset(
          BigNumber.sum(
            o.delegations.aggregates?.sum?.upcomingAmount ?? 0,
            o.upcomingDelegatingValidators.aggregates?.sum?.upcomingAmount ?? 0,
          ),
          'Flip',
        );

        let overdelegation =
          totalUpcomingDelegation.gt(0) &&
          o.upcomingAuthorities.totalCount === o.qualifiedValidators.totalCount
            ? totalUpcomingStake
                .add(totalUpcomingDelegation)
                .sub(auction.minActiveBid?.value.times(o.upcomingAuthorities.totalCount) ?? 0)
            : TokenAmount.ZERO;

        let requiredDelegation = TokenAmount.ZERO;

        // if an operator has no nodes projected to make it into the authority set,
        // we have to modify the calculations to have at least one node
        if (totalUpcomingStake.eq(0)) {
          const averageStake = new BigNumber(
            o.upcomingDelegatingValidators.aggregates?.sum?.upcomingAmount ?? 0,
          ).dividedBy(o.upcomingDelegatingValidators.totalCount || 1);

          totalUpcomingStake = TokenAmount.fromAsset(averageStake, 'Flip');

          totalUpcomingDelegation = totalUpcomingDelegation.sub(totalUpcomingStake);

          overdelegation = TokenAmount.ZERO;

          requiredDelegation = (auction.minActiveBid ?? TokenAmount.ZERO)
            .sub(totalUpcomingStake)
            .sub(totalUpcomingDelegation)
            // TODO: remove this line when we know how to show required delegation properly
            .min(TokenAmount.ZERO);
        }

        const apyMap = new Map(
          (epochs ?? []).reduce<[epochId: number, apyBp: number][]>((acc, epoch) => {
            const apyBp = epoch.apys.nodes.find(
              (a) => a.operator?.account.idSs58 === o.idSs58,
            )?.apyBp;
            if (apyBp !== undefined) acc.push([epoch.id, apyBp]);
            return acc;
          }, []),
        );

        let weightedDelegatorApy: DelegationApy = { type: 'loading' };

        if (epochs && apyMap) {
          if (apyMap.size === 0) {
            weightedDelegatorApy = { type: 'n/a' };
          } else {
            const totalApy =
              epochs.reduce((acc, { id: epochId }, index) => {
                const apyBp = apyMap.get(epochId) ?? 0;
                return acc + apyBp * weights[index];
              }, 0) / weights.reduce((a, b) => a + b, 0);
            weightedDelegatorApy = { type: 'apyBp', apyBp: Math.round(totalApy) };
          }
        }

        return {
          idSs58: o.idSs58 as `cF${string}`,
          exceptionList: o.exceptionList as `cF${string}`[],
          alias: o.account.alias,
          isDelegationAllowed,
          openDelegationEnabled: o.openDelegationEnabled,
          delegations: o.delegations,
          allValidators: o.allValidators,
          onlineValidators: o.onlineValidators.totalCount,
          overdelegation: overdelegation.gt(auction.blockReward.value) ? overdelegation : undefined,
          requiredDelegation: requiredDelegation.gt(0) ? requiredDelegation : undefined,
          weightedDelegatorApy,
          currentEpoch: {
            delegatorApy: currentEpochDelegatorApy,
            delegators: new Set(
              o.currentDelegators.nodes.map((d) => d.delegatorIdSs58 as `cF${string}`),
            ),
            commissionBps: o.activeDelegationFeeBps,
            authorities: o.currentAuthorities.totalCount,
            operatorApy: currentEpochOperatorApy,
          },
          nextEpoch: {
            // TODO(post FLIP2.1): remove all old apy calculations
            delegatorApy: (totalUpcomingDelegation.eq(0)
              ? { type: 'n/a' }
              : {
                  type: 'apyPct',
                  apyPct: calculateDelegatorApy(
                    auction,
                    TokenAmount.ZERO,
                    o.upcomingAuthorities.totalCount,
                    totalUpcomingStake,
                    totalUpcomingDelegation,
                    o.upcomingDelegationFeeBps,
                    cacheData.upcomingAuthorities!.totalCount,
                  ),
                }) satisfies DelegationApy,
            calculateDelegatorApy: (amount: TokenAmount | undefined) => {
              // if the operator does not have enough FLIP to get one node in the authority set
              const totalStake = TokenAmount.fromAsset(
                o.allValidators.aggregates?.sum?.totalBalance ?? 0,
                'Flip',
              );

              if (
                o.allValidators.totalCount === 0 ||
                (o.upcomingAuthorities.totalCount === 0 &&
                  totalStake
                    .add(totalUpcomingDelegation)
                    .add(amount ?? 0)
                    .lt(auction.minActiveBid ?? Infinity))
              ) {
                return 0;
              }

              // if this operator has at least one node but not enough stake to get into the authority set
              if (totalUpcomingStake.eq(0)) {
                // calculate how many nodes they would get with the additional delegation
                const numberOfNodes = totalStake
                  .add(totalUpcomingDelegation)
                  .add(amount ?? 0)
                  .value.div(auction.minActiveBid?.value ?? 1)
                  .integerValue(BigNumber.ROUND_DOWN)
                  .toNumber();

                // if they still can't get any nodes, return 0
                if (numberOfNodes === 0) return 0;

                return calculateDelegatorApy(
                  auction,
                  !amount || amount.eq(0) ? FALLBACK_ADDTL_DELEGATION : amount,
                  numberOfNodes,
                  totalStake,
                  totalUpcomingDelegation,
                  o.upcomingDelegationFeeBps,
                  cacheData.upcomingAuthorities!.totalCount,
                );
              }

              return calculateDelegatorApy(
                auction,
                !amount || amount.eq(0) ? FALLBACK_ADDTL_DELEGATION : amount,
                o.upcomingAuthorities.totalCount ||
                  Math.min(o.upcomingDelegatingValidators.totalCount, 1),
                totalUpcomingStake,
                totalUpcomingDelegation,
                o.upcomingDelegationFeeBps,
                cacheData.upcomingAuthorities!.totalCount,
              );
            },
            commissionBps: o.upcomingDelegationFeeBps,
            authorities: o.upcomingAuthorities.totalCount,
            totalStake: totalUpcomingStake,
            totalDelegation: totalUpcomingDelegation,
          },
        };
      });

    return { auction, operators, isLoading };
  }, [cacheData, delegatorIdSs58, isLoading]);
}
